import {
  IcosahedronGeometry,
  Mesh,
  TetrahedronGeometry,
  TorusGeometry,
  Vector3
} from 'three';
import { Ability, AbilityPhase } from './Ability.js';
import { ParticleShape } from '../particles/ParticleSystem.js';
import { RateEmitter } from '../particles/ParticleEngine.js';
import { settings } from '../config/settings.js';
import { frame } from '../core/FrameUniforms.js';
import { getColor } from '../utils/color.js';
import { Easing, saturate } from '../utils/math.js';
import { BurstMode } from '../effects/BurstSphere.js';
import { DecalType } from '../effects/GroundDecals.js';
import {
  createCataclysmMaterial,
  syncCataclysmMaterial
} from '../materials/CataclysmMaterial.js';
import {
  commitConstructInstances,
  createConstruct,
  disposeConstruct,
  emitParticles,
  setConstructInstance,
  syncConstruct
} from './NovelSpellUtils.js';
import { ConstructStyle } from '../materials/ConstructMaterial.js';

const CHARGE_PHASE = 'charge';
const MAX_FRAGMENTS = 30;
const MAX_RINGS = 5;
const _up = new Vector3(0, 1, 0);
const _pos = new Vector3();
const _dir = new Vector3();
const _scale = new Vector3();
const _rotation = new Vector3();
const _emit = {};

/**
 * A hold-and-release projectile. Charge is a first-class phase rather than a
 * long travel animation: App keeps the target live, this instance follows the
 * caster, and release commits the current charge to the projectile and blast.
 */
export class CataclysmFireballAbility extends Ability {
  constructor(context) {
    super('cataclysm', context);
    this.charge = 0;
    this.chargeAge = 0;
    this.releasedCharge = 0;
    this.chargeStage = 0;
    this.visualPosition = new Vector3();
    this.flameEmitter = new RateEmitter();
    this.arcaneEmitter = new RateEmitter();
    this.smokeEmitter = new RateEmitter();
  }

  createShaders() {
    const sphereGeometry = new IcosahedronGeometry(1, 5);
    this.coreMaterial = createCataclysmMaterial(0);
    this.core = new Mesh(sphereGeometry, this.coreMaterial);
    this.core.frustumCulled = false;
    this.core.renderOrder = 15;

    this.auraMaterial = createCataclysmMaterial(1);
    this.aura = new Mesh(sphereGeometry.clone(), this.auraMaterial);
    this.aura.frustumCulled = false;
    this.aura.renderOrder = 14;

    this.outerMaterial = createCataclysmMaterial(1.8);
    this.outer = new Mesh(sphereGeometry.clone(), this.outerMaterial);
    this.outer.frustumCulled = false;
    this.outer.renderOrder = 13;

    this.rings = createConstruct(
      new TorusGeometry(1, 0.018, 6, 96),
      ConstructStyle.ENERGY,
      { count: MAX_RINGS, renderOrder: 16 }
    );
    this.fragments = createConstruct(
      new TetrahedronGeometry(0.16, 0),
      ConstructStyle.PRISM,
      { count: MAX_FRAGMENTS, renderOrder: 16 }
    );

    this.group.add(this.outer, this.aura, this.core, this.rings.mesh, this.fragments.mesh);
  }

  createParticles() {
    this.flames = this.ctx.particles.get('cataclysm.flames', {
      capacity: 3200,
      shape: ParticleShape.SOFT,
      additive: true,
      curl: true,
      stretch: true,
      swirl: true
    });
    this.arcane = this.ctx.particles.get('cataclysm.arcane', {
      capacity: 2600,
      shape: ParticleShape.STREAK,
      additive: true,
      curl: true,
      stretch: true,
      swirl: true
    });
    this.smoke = this.ctx.particles.get('cataclysm.smoke', {
      capacity: 900,
      shape: ParticleShape.SMOKE,
      additive: false,
      curl: true,
      softFade: 0.8
    });
  }

  get impactDuration() {
    return settings.cataclysm.impactTime * settings.global.lifetime;
  }

  get fadeDuration() {
    return settings.cataclysm.fadeTime * settings.global.lifetime;
  }

  get instanceCount() {
    return this.rings.mesh.count + this.fragments.mesh.count;
  }

  onSpawn() {
    this.charge = 0;
    this.chargeAge = 0;
    this.releasedCharge = 0;
    this.chargeStage = 0;
    this.flameEmitter.reset();
    this.arcaneEmitter.reset();
    this.smokeEmitter.reset();
    this._setVisualVisible(true);
    this._syncChargePosition();
    this._updateVisuals(0, 1);
  }

  /** Enter the custom phase while retaining Ability's pooled reset contract. */
  beginCharge(origin) {
    this.spawn(origin, _dir.set(0, 0, 1), 1);
    this.phase = CHARGE_PHASE;
  }

  /** Follow the moving character without changing the aim controller's target. */
  syncChargeOrigin(origin) {
    if (this.phase !== CHARGE_PHASE) return;
    this.origin.set(origin.x, 0, origin.z);
    this._syncChargePosition();
  }

  release(origin, direction, distance) {
    if (this.phase !== CHARGE_PHASE) return false;
    this.origin.set(origin.x, 0, origin.z);
    this.direction.copy(direction).setY(0).normalize();
    this.side.crossVectors(this.direction, _up).normalize();
    this.length = Math.max(0.1, distance);
    this.releasedCharge = Math.max(settings.cataclysm.minimumCharge, this.charge);
    this.front = 0;
    this.u = 0;
    this.age = 0;
    this.phase = AbilityPhase.TRAVEL;
    this.lightBoost = settings.cataclysm.releaseLight * this.releasedCharge;
    this.ctx.flash.trigger(getColor(settings.cataclysm.colorWhite), 0.05 + this.releasedCharge * 0.12);
    this._releaseParticles();
    return true;
  }

  update(dt) {
    if (this.phase !== CHARGE_PHASE) {
      super.update(dt);
      return;
    }
    this.age += dt;
    this.chargeAge += dt;
    const c = settings.cataclysm;
    this.charge = saturate(this.chargeAge / Math.max(0.1, c.maxChargeTime));
    this.position.copy(this.visualPosition);
    this._updateVisuals(this.charge, 1);
    this._chargeParticles(dt);
    this._chargeFeedback(dt);
    this._updateLight(dt, 0.08 + Easing.inQuad(this.charge) * 1.12);
  }

  _syncChargePosition() {
    const c = settings.cataclysm;
    const tremor = this.charge * this.charge * c.instability;
    this.visualPosition.set(
      this.origin.x + Math.sin(this.age * 17.1) * tremor,
      c.headHeight + Math.sin(this.age * (8 + this.charge * 13)) * tremor * 0.55,
      this.origin.z + Math.cos(this.age * 14.3) * tremor
    );
    this.position.copy(this.visualPosition);
  }

  _radius(charge = this.charge) {
    const c = settings.cataclysm;
    return c.startRadius + (c.maxRadius - c.startRadius) * Easing.inOutCubic(charge);
  }

  _updateVisuals(charge, fade) {
    const c = settings.cataclysm;
    const radius = this._radius(charge);
    const unstable = charge * charge;
    const pulse = 1 + Math.sin(this.age * (7 + charge * 16)) * (0.025 + unstable * 0.055);

    this.core.position.copy(this.visualPosition);
    this.core.scale.setScalar(radius * pulse);
    this.core.updateMatrix();
    this.aura.position.copy(this.visualPosition);
    this.aura.scale.setScalar(radius * (1.28 + unstable * 0.16) * (2 - pulse));
    this.aura.updateMatrix();
    this.outer.position.copy(this.visualPosition);
    this.outer.scale.setScalar(radius * (1.62 + unstable * 0.42));
    this.outer.updateMatrix();
    this.outer.visible = charge > 0.18 && fade > 0.01;

    syncCataclysmMaterial(this.coreMaterial, c, charge, fade);
    syncCataclysmMaterial(this.auraMaterial, c, charge, fade * (0.72 + unstable * 0.22));
    syncCataclysmMaterial(this.outerMaterial, c, charge, fade * unstable * 0.55);

    const ringCount = Math.min(MAX_RINGS, 1 + Math.floor(charge * MAX_RINGS));
    for (let i = 0; i < ringCount; i++) {
      const angle = this.age * (1.3 + charge * 5.2) * (i % 2 ? -1 : 1) + i * 1.71;
      const ringRadius = radius * (1.32 + i * 0.13);
      _pos.copy(this.visualPosition);
      _scale.set(ringRadius, ringRadius, ringRadius);
      _rotation.set(Math.PI * 0.5 + Math.sin(angle * 0.37) * 0.65, angle, angle * 0.31);
      setConstructInstance(this.rings.mesh, i, _pos, _scale, _rotation);
    }
    commitConstructInstances(this.rings.mesh, ringCount);
    syncConstruct(this.rings, c, { pulse: unstable, fade, seed: 7.3 });

    const fragmentCount = Math.min(MAX_FRAGMENTS, Math.floor(charge * 34));
    for (let i = 0; i < fragmentCount; i++) {
      const lane = i % 3;
      const angle = i * 2.399 + this.age * (1.6 + charge * 5.8) * (lane === 1 ? -1 : 1);
      const orbit = radius * (1.55 + lane * 0.42 + 0.12 * Math.sin(i * 3.1 + this.age));
      _pos.set(
        this.visualPosition.x + Math.cos(angle) * orbit,
        this.visualPosition.y + Math.sin(angle * (1.4 + lane * 0.2)) * radius * (0.55 + lane * 0.18),
        this.visualPosition.z + Math.sin(angle) * orbit
      );
      const size = radius * (0.055 + (i % 5) * 0.009) * (0.7 + charge * 0.7);
      _scale.setScalar(size);
      _rotation.set(angle * 0.7, angle * 1.3, angle * 0.4);
      setConstructInstance(this.fragments.mesh, i, _pos, _scale, _rotation);
    }
    commitConstructInstances(this.fragments.mesh, fragmentCount);
    syncConstruct(this.fragments, c, { pulse: unstable, fade, seed: 19.1 });
  }

  _chargeParticles(dt) {
    const c = settings.cataclysm;
    const q = this.charge;
    const radius = this._radius();
    this._syncParticleMaterials(q);

    const flames = this.flameEmitter.tick(dt, c.flameRate * (0.15 + q * q * 0.95));
    emitParticles(this.flames, _emit, {
      position: this.visualPosition,
      direction: _up,
      count: flames,
      radius: radius * (0.75 + q * 0.4),
      speed: c.flameSpeed * (0.5 + q),
      spread: 0.9,
      size: c.particleSize * (0.65 + q * 0.8),
      life: c.particleLife,
      spin: 5 + q * 11,
      anchor: this.visualPosition
    });

    const arcane = this.arcaneEmitter.tick(dt, c.arcaneRate * Math.max(0, q - 0.12) * 1.35);
    _dir.set(Math.sin(this.age * 5), 0.3, Math.cos(this.age * 5)).normalize();
    emitParticles(this.arcane, _emit, {
      position: this.visualPosition,
      direction: _dir,
      count: arcane,
      radius: radius * (1.35 + q * 0.75),
      speed: c.arcaneSpeed * (0.6 + q * 1.6),
      spread: 1,
      size: c.particleSize * 0.65,
      life: c.particleLife * 0.75,
      spin: 8 + q * 18,
      anchor: this.visualPosition
    });

    if (q > 0.58) {
      const smoke = this.smokeEmitter.tick(dt, c.smokeRate * (q - 0.5));
      emitParticles(this.smoke, _emit, {
        position: this.visualPosition,
        direction: _up,
        count: smoke,
        radius: radius * 0.8,
        speed: 0.8 + q,
        spread: 0.8,
        size: radius * 0.32,
        life: 1.1,
        spin: 1.8
      });
    }
  }

  _chargeFeedback(dt) {
    const stage = Math.min(4, Math.floor(this.charge * 4.001));
    if (stage > this.chargeStage) {
      this.chargeStage = stage;
      emitParticles(this.arcane, _emit, {
        position: this.visualPosition,
        direction: _up,
        count: 34 + stage * 18,
        radius: this._radius() * 0.4,
        speed: 4 + stage * 1.5,
        spread: 1,
        size: settings.cataclysm.particleSize * 0.8,
        life: 0.65,
        spin: 14
      });
      this.lightBoost += stage * 1.6;
      if (stage >= 3) this.ctx.shake.add(0.05 * stage * settings.global.cameraShake, 0.18);
    }
    if (this.charge > 0.72) {
      this.ctx.shake.rumble((this.charge - 0.7) * settings.cataclysm.holdRumble * settings.global.cameraShake, dt);
    }
  }

  _syncParticleMaterials(charge) {
    const c = settings.cataclysm;
    this.flames.setGradient(getColor(c.colorWhite), getColor(c.colorYellow), getColor(c.colorOrange), getColor(c.colorRed));
    this.arcane.setGradient(getColor(c.colorWhite), getColor(c.colorBlue), getColor(c.colorPurple), getColor(c.colorOrange));
    this.smoke.setGradient(getColor(c.colorPurple), getColor(c.colorRed), getColor(c.colorSmoke), getColor(c.colorSmokeDark));
    this.flames.uniforms.uSwirl.value = 4 + charge * 15;
    this.flames.uniforms.uTurbulence.value = 0.6 + charge * 2.4;
    this.arcane.uniforms.uSwirl.value = 8 + charge * 24;
    this.arcane.uniforms.uTurbulence.value = 0.8 + charge * 2.8;
  }

  _releaseParticles() {
    this._syncParticleMaterials(this.releasedCharge);
    emitParticles(this.arcane, _emit, {
      position: this.visualPosition,
      direction: this.direction,
      count: 120 + this.releasedCharge * 180,
      radius: this._radius(this.releasedCharge) * 0.65,
      speed: 7 + this.releasedCharge * 10,
      spread: 0.9,
      size: settings.cataclysm.particleSize,
      life: 0.8,
      spin: 18
    });
  }

  _projectilePoint(u, out) {
    out.copy(this.origin).addScaledVector(this.direction, this.length * u);
    const startY = settings.cataclysm.headHeight;
    out.y = startY * (1 - u) + Math.sin(Math.PI * u) * settings.cataclysm.arcHeight;
    return out;
  }

  onTravel(dt) {
    const c = settings.cataclysm;
    this._projectilePoint(this.u, this.visualPosition);
    this.position.copy(this.visualPosition);
    this._updateVisuals(this.releasedCharge, 1);
    this._syncParticleMaterials(this.releasedCharge);
    _dir.copy(this.direction).multiplyScalar(-1).setY(0.28).normalize();
    emitParticles(this.flames, _emit, {
      position: this.visualPosition,
      direction: _dir,
      count: this.flameEmitter.tick(dt, c.travelRate),
      radius: this._radius(this.releasedCharge) * 0.72,
      speed: c.travelTrailSpeed,
      spread: 0.82,
      size: c.particleSize * (1 + this.releasedCharge),
      life: 0.75,
      spin: 12,
      anchor: this.visualPosition
    });
    emitParticles(this.arcane, _emit, {
      position: this.visualPosition,
      direction: _dir,
      count: this.arcaneEmitter.tick(dt, c.travelRate * 0.7),
      radius: this._radius(this.releasedCharge),
      speed: c.travelTrailSpeed * 1.4,
      spread: 1,
      size: c.particleSize * 0.65,
      life: 0.55,
      spin: 20,
      anchor: this.visualPosition
    });
    this.ctx.shake.rumble(c.flightRumble * this.releasedCharge * settings.global.cameraShake, dt);
  }

  onImpact() {
    const c = settings.cataclysm;
    const q = this.releasedCharge;
    const power = 0.45 + q * 0.85;
    this.pointAt(1, this.position);
    _pos.copy(this.position).setY(0.35 + q * 0.65);
    this._setVisualVisible(false);

    this.ctx.bursts.spawn(BurstMode.FIRE, _pos, {
      radius: 0.45 + q,
      endRadius: c.blastRadius * power,
      life: 0.72 + q * 0.45,
      intensity: c.blastIntensity * power,
      opacity: 1,
      fresnel: 1.35,
      displace: 0.7,
      turbulence: 1.4 + q * 1.8,
      colorA: getColor(c.colorWhite),
      colorB: getColor(c.colorOrange),
      colorC: getColor(c.colorPurple)
    });
    this.ctx.bursts.spawn(BurstMode.FIRE, _pos, {
      radius: 0.18,
      endRadius: c.blastRadius * power * 0.62,
      life: 0.34,
      intensity: c.blastIntensity * 2.1,
      opacity: 1,
      fresnel: 0.8,
      displace: 0.25,
      colorA: getColor(c.colorWhite),
      colorB: getColor(c.colorBlue),
      colorC: getColor(c.colorYellow)
    });
    this.ctx.decals.spawn(DecalType.SHOCKWAVE, this.position, {
      radius: c.shockRadius * power,
      life: 0.9,
      width: 0.1,
      intensity: 1.8,
      colorA: getColor(c.colorPurple),
      colorB: getColor(c.colorWhite)
    });
    this.ctx.decals.spawn(DecalType.SCORCH, this.position, {
      radius: c.scorchRadius * power,
      life: c.scorchLife,
      intensity: 0.72,
      colorA: getColor(c.colorSmokeDark),
      colorB: getColor(c.colorRed),
      height: 0.018
    });
    this.ctx.fissures.spawn(this.position, {
      radius: c.fissureRadius * power,
      life: c.scorchLife
    });
    emitParticles(this.flames, _emit, {
      position: _pos,
      direction: _up,
      count: 280 + q * 420,
      radius: 0.8 + q,
      speed: 9 + q * 13,
      spread: 1,
      size: c.particleSize * (1.3 + q),
      life: 1.1,
      spin: 16
    });
    emitParticles(this.arcane, _emit, {
      position: _pos,
      direction: _up,
      count: 220 + q * 360,
      radius: 0.65,
      speed: 13 + q * 17,
      spread: 1,
      size: c.particleSize,
      life: 0.9,
      spin: 24
    });
    this.lightBoost = c.impactLight * power;
    this.ctx.shake.add(c.impactShake * power * settings.global.cameraShake, c.shakeDuration);
    this.ctx.flash.trigger(getColor(c.colorWhite), c.impactFlash * power);
  }

  onFade(dt, t) {
    if (t > 1.4) return;
    const c = settings.cataclysm;
    emitParticles(this.smoke, _emit, {
      position: _pos.copy(this.position).setY(0.3),
      direction: _up,
      count: this.smokeEmitter.tick(dt, c.aftermathSmoke * Math.max(0, 1.4 - t)),
      radius: c.scorchRadius * 0.45,
      speed: 1.8,
      spread: 0.85,
      size: 1.2 + this.releasedCharge,
      life: 1.6,
      spin: 1.4
    });
  }

  _setVisualVisible(visible) {
    this.core.visible = visible;
    this.aura.visible = visible;
    if (!visible) {
      this.outer.visible = false;
      commitConstructInstances(this.rings.mesh, 0);
      commitConstructInstances(this.fragments.mesh, 0);
    }
  }

  onDestroy() {
    this._setVisualVisible(false);
    this.flameEmitter.reset();
    this.arcaneEmitter.reset();
    this.smokeEmitter.reset();
  }

  dispose() {
    this.core.geometry.dispose();
    this.aura.geometry.dispose();
    this.outer.geometry.dispose();
    this.coreMaterial.dispose();
    this.auraMaterial.dispose();
    this.outerMaterial.dispose();
    this.rings.mesh.geometry.dispose();
    this.fragments.mesh.geometry.dispose();
    disposeConstruct(this.rings);
    disposeConstruct(this.fragments);
    super.dispose();
  }
}
